import type { TCountryCode } from "countries-list";
import { AnimatePresence, motion } from "motion/react";
import { graphql, useStaticQuery } from "gatsby";
import { useTranslation } from "gatsby-plugin-react-i18next";
import ISO6391 from "iso-639-1";
import { useCallback, useState, type FC } from "react";
import Footer from "../../components/Footer";
import Credits from "../../components/Footer/components/Credits";
import { CreditText } from "../../components/Footer/components/Credits/Credits";
import FooterBlock from "../../components/Footer/components/FooterBlock";
import FooterBlockItem from "../../components/Footer/components/FooterBlockItem";
import FooterSocials from "../../components/Footer/components/FooterSocials";
import LinkListSection from "../../components/Footer/components/LinkListSection";
import LinkList from "../../components/Footer/components/LinkListSection/components/LinkList";
import LinkListItem from "../../components/Footer/components/LinkListSection/components/LinkListItem";
import LogoLanguageBar from "../../components/Footer/components/LogoLanguageBar";
import Newsletter from "../../components/Newsletter";
import Button from "../../components/general/Button";
import { getFlag } from "../../components/general/Flag/Flag";
import Icon from "../../components/general/Icon";
import type { IconType } from "../../components/general/Icon/Icon";
import Link from "../../components/general/Link";
import Logo from "../../components/general/Logo";
import Modal from "../../components/general/Modal";
import ModalOverlay from "../../components/general/Modal/components/ModalOverlay/ModalOverlay";
import SidebarSiteSelectionListItem from "../../components/general/Modal/components/SidebarSiteSelectionListItem";
import ModalPortal from "../../components/general/ModalPortal";
import Payment from "../../components/general/Payment";
import type { PaymentItemType } from "../../components/general/Payment/Payment";
import { AppDispatchContext, useAppState } from "../../context/app/context";
import { useDispatchContext } from "../../hooks/use-dispatch-context";
import { openCookieSettings } from "../../utils/cookies/cookie-service";
import { getUrlFromStoryblokLink } from "../../utils/storyblok/urls";

const SiteFooterContainer: FC = () => {
	const { siteConfig, userCountry, allowedCountries } = useAppState();

	const appDispatch = useDispatchContext(AppDispatchContext);

	const paymentMethods = siteConfig.payment_methods.map(
		(payment_method) => payment_method as PaymentItemType,
	);
	const [languageCode] = siteConfig.language.split("-");

	const language = ISO6391.getNativeName(languageCode ?? "");

	const data = useStaticQuery<Queries.FooterQuery>(query);

	const { t } = useTranslation();

	if (
		!data.navigation?.newsletter ||
		!data.navigation.contact_us ||
		!data.navigation.socials ||
		!data.navigation.link_lists ||
		!data.navigation.sitemap_link ||
		!data.navigation.privacy_policy_link ||
		!data.navigation.terms_and_conditions_link ||
		!data.navigation.copyright_text
	) {
		throw new Error("Missing data in footer query");
	}

	const {
		newsletter,
		contact_us,
		socials,
		link_lists,
		sitemap_link,
		privacy_policy_link,
		terms_and_conditions_link,
		copyright_text,
	} = data.navigation;

	const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);

	const setCountryAndCloseModal = useCallback(
		(country: TCountryCode) => {
			appDispatch({ type: "setUserCountry", userCountry: country });
			setIsLanguageModalOpen(false);
		},
		[appDispatch],
	);

	return (
		<>
			<Footer>
				<LogoLanguageBar>
					<Logo linkLabel={t("Link")} />

					<Button
						variant="container"
						iconLeft
						icon={getFlag(allowedCountries, userCountry)}
						label={language}
						onClick={() => setIsLanguageModalOpen(true)}
					/>
				</LogoLanguageBar>
				<FooterBlock>
					<FooterBlockItem
						type="email"
						emailForm={
							<Newsletter
								list_id={siteConfig.klaviyo_id}
								placeholder={t("Newsletter Email")}
							/>
						}
						headline={newsletter[0]?.title || ""}
						content={newsletter[0]?.text || ""}
					/>
					<FooterBlockItem
						type="cta"
						buttonText={t("To Help Center")}
						headline={contact_us[0]?.title || ""}
						content={contact_us[0]?.text || ""}
						href={getUrlFromStoryblokLink(contact_us[0]?.link)}
						target={contact_us[0]?.link?.target}
					/>
				</FooterBlock>
				<FooterSocials text={socials[0]?.desktop_text || ""}>
					{socials[0]?.social_list &&
						socials[0].social_list.map((social, linkId) => {
							return (
								<Link
									key={`social-link-${linkId}`}
									href={getUrlFromStoryblokLink(social?.link)}
									target="_blank"
									aria-label={`${t("Visit Babybjörn on")}- ${social?.icon}`}
								>
									<Icon icon={social?.icon as IconType} variant="social" />
								</Link>
							);
						})}
				</FooterSocials>

				<LinkListSection>
					{link_lists.map((list) => (
						<LinkList key={list?._uid} title={list?.title || ""}>
							{list &&
								list.links?.map((link) => (
									<LinkListItem
										target={link?.link?.target}
										key={link?._uid}
										linkText={link?.label || ""}
										href={getUrlFromStoryblokLink(link?.link)}
									/>
								))}
						</LinkList>
					))}
				</LinkListSection>
				<Credits>
					<LinkList privacySection={true}>
						<LinkListItem
							linkText={t("Cookie settings")}
							onClick={() => openCookieSettings()}
						/>
						<LinkListItem
							linkText={t("Sitemap")}
							href={getUrlFromStoryblokLink(sitemap_link)}
						/>
						<LinkListItem
							linkText={t("Privacy Policy")}
							href={getUrlFromStoryblokLink(privacy_policy_link)}
						/>
						<LinkListItem
							linkText={t("User terms and conditions")}
							href={getUrlFromStoryblokLink(terms_and_conditions_link)}
						/>
					</LinkList>
					{copyright_text && <CreditText>{copyright_text}</CreditText>}
				</Credits>
				<Payment>
					{paymentMethods.map((provider) => (
						<Icon key={provider} icon={provider} width={55} height={48} />
					))}
				</Payment>
			</Footer>
			<ModalPortal show={isLanguageModalOpen}>
				<AnimatePresence>
					{isLanguageModalOpen && (
						<>
							<ModalOverlay onClick={() => setIsLanguageModalOpen(false)} />
							<motion.div
								initial={{ x: "100%" }}
								animate={{ x: 0 }}
								exit={{ x: "100%" }}
								transition={{ type: "spring", bounce: 0, duration: 0.4 }}
								style={{ position: "fixed", top: 0, right: 0, zIndex: 101 }}
							>
								<Modal
									closeButtonLabel={t("Close")}
									overlayOnClick={() => setIsLanguageModalOpen(false)}
									buttonOnClick={() => setIsLanguageModalOpen(false)}
								>
									<SidebarSiteSelectionListItem
										setCountryAndCloseModal={setCountryAndCloseModal}
									/>
								</Modal>
							</motion.div>
						</>
					)}
				</AnimatePresence>
			</ModalPortal>
		</>
	);
};

export default SiteFooterContainer;

const query = graphql`
	query Footer {
		siteConfigs: allParsedSiteConfig {
			nodes {
				countries
				domain
				language
			}
		}
		navigation {
			newsletter {
				title
				text
			}
			contact_us {
				title
				text
				link {
					...Link
				}
			}
			link_lists {
				_uid
				title
				links {
					label
					_uid
					link {
						...Link
					}
				}
			}
			socials {
				social_list {
					icon
					link {
						...Link
					}
				}
				desktop_text
			}
			sitemap_link {
				...Link
			}
			privacy_policy_link {
				...Link
			}
			terms_and_conditions_link {
				...Link
			}
			copyright_text
		}
	}
`;
